import { useEffect, useState, type FormEvent, type SVGProps, type ComponentType } from "react";
import { useNavigate } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { TextField } from "../components/ui";
import { AuthSubmit } from "../components/AuthSubmit";
import { IconGithub, IconGoogle, IconYandex, IconKey } from "../components/icons";
import { useAuth } from "../AuthContext";
import { api, type SsoProvider } from "../api";
import { isValidEmail } from "../validation";
import { useI18n } from "../i18n";
import { ssoProviderAllowlist } from "../flags";
import { useAuthNav } from "../authTransition";
import { useFormError } from "../useFormError";
import "./auth.css";

type IconCmp = ComponentType<SVGProps<SVGSVGElement>>;

const PROVIDER_ICONS: Record<string, IconCmp> = {
  github: IconGithub,
  google: IconGoogle,
  yandex: IconYandex,
};

const iconForProvider = (p: SsoProvider): IconCmp =>
  PROVIDER_ICONS[p.provider_key] ?? IconKey;

export default function WelcomePage() {
  const nav = useNavigate();
  const { go } = useAuthNav();
  const { user, setPendingEmail } = useAuth();
  const { t, lang } = useI18n();
  const [email, setEmail] = useState("");
  const { error, setError, formRef } = useFormError();
  const [providers, setProviders] = useState<SsoProvider[]>([]);
  const [ssoBusy, setSsoBusy] = useState<string | null>(null);

  useEffect(() => {
    if (user) nav("/overview", { replace: true });
  }, [user, nav]);

  useEffect(() => {
    let cancelled = false;
    api
      .ssoProviders()
      .then(({ providers: items }) => {
        // The backend already returns only enabled providers — no client filter needed.
        if (!cancelled) setProviders(items);
      })
      .catch(() => {
        if (!cancelled) setProviders([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const validateEmail = (): string | null => {
    const normalized = email.trim();
    if (!isValidEmail(normalized)) {
      setError(t("auth.invalidEmail"));
      return null;
    }
    setError(null);
    return normalized;
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const normalized = validateEmail();
    if (!normalized) return;
    setPendingEmail(normalized);
    go("/password");
  };

  const onSignup = () => {
    const normalized = email.trim();
    if (normalized && normalized.includes("@")) {
      setPendingEmail(normalized);
    } else {
      setPendingEmail(null);
    }
    setError(null);
    go("/signup");
  };

  const ssoAllow = ssoProviderAllowlist(lang);
  const visibleProviders = providers.filter((p) =>
    ssoAllow.has(p.provider_key),
  );

  const handleSso = (provider: SsoProvider) => async () => {
    setSsoBusy(provider.provider_key);
    setError(null);
    try {
      const { redirect_url } = await api.startSso(provider.provider_key);
      window.location.assign(redirect_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.ssoFailed"));
      setSsoBusy(null);
    }
  };

  return (
    <AuthLayout title={t("auth.welcomeTitle")}>
      <form ref={formRef} className="auth__form anim-stagger" onSubmit={onSubmit} noValidate>
        <TextField
          type="email"
          name="email"
          autoComplete="username"
          placeholder={t("auth.emailPlaceholder")}
          focused
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
        />
        {error ? <p className="auth-error">{error}</p> : null}
        <AuthSubmit>{t("auth.login")}</AuthSubmit>
        <p className="auth-signup">
          <button type="button" onClick={onSignup}>{t("auth.signUp")}</button>
        </p>
      </form>

      {visibleProviders.length > 0 ? (
        <div className="auth-socials">
          {visibleProviders.map((provider) => {
            const Icon = iconForProvider(provider);
            const label = t("auth.continueWith", { provider: provider.display_name });
            return (
              <button
                key={provider.provider_key}
                type="button"
                aria-label={label}
                title={label}
                onClick={handleSso(provider)}
                disabled={ssoBusy !== null}
              >
                <Icon width={24} height={24} />
              </button>
            );
          })}
        </div>
      ) : null}
    </AuthLayout>
  );
}
